'use es6';

import Immutable from 'immutable';
import { RequestStatus } from '../Constants';
import * as RequestType from '../enums/RequestType';
import { escapeRegExp } from '../utils/stringUtils';
import { ADD_FOLDER_SUCCEEDED, BULK_DELETE_ATTEMPTED, BULK_DELETE_FAILED, BULK_MOVE_ATTEMPTED, BULK_MOVE_FAILED, BULK_MOVE_SUCCEEDED, DELETE_FOLDER_SUCCEEDED, FETCH_FOLDERS_ATTEMPTED, FETCH_FOLDERS_FAILED, FETCH_FOLDERS_SUCCEEDED, MOVE_FOLDER_SUCCEEDED, RENAME_FOLDER_SUCCEEDED, CHANGE_FOLDER_PARTITIONING_SUCCEEDED, SEARCH_FILES_AND_FOLDERS_ATTEMPTED, SEARCH_FILES_AND_FOLDERS_FAILED, SEARCH_FILES_AND_FOLDERS_SUCCESS } from '../actions/ActionTypes';
const defaultState = Immutable.Map({
  objects: new Immutable.OrderedMap(),
  total: 0,
  status: RequestStatus.UNINITIALIZED,
  requests: Immutable.fromJS({
    [RequestType.byParentId]: {},
    [RequestType.bySearchTerm]: {}
  })
});
function getPathMatcher(folder) {
  return new RegExp(`^${escapeRegExp(folder.get('full_path'))}\\/`);
}
function findIndex(state, folderId) {
  if (state.get('objects') instanceof Immutable.OrderedMap) {
    return folderId;
  }
  return state.get('objects').findIndex(item => {
    return item.get('id') === folderId;
  });
}
function replaceFolder(state, folder) {
  const index = findIndex(state, folder.get('id'));
  return state.setIn(['objects', index], folder);
}
function deleteFolder(state, folder) {
  const objects = state.get('objects').delete(folder.get('id'));
  return state.merge({
    objects,
    total: state.get('total') - 1
  });
}
function deleteAll(state, folders) {
  return folders.reduce(deleteFolder, state);
}
function move(state, folders, destination) {
  folders.forEach(folder => {
    state = state.mergeIn(['objects', folder.get('id')], {
      full_path: `${destination.get('full_path')}/${folder.get('name')}`,
      parent_folder_id: destination.get('id'),
      updated: Date.now()
    });
  });
  return state;
}
function handleMove(state, folders, updatedFolders) {
  const toRemove = folders.filter(folder => {
    return updatedFolders.findIndex(updatedFolder => updatedFolder.get('id') === folder.get('id')) === -1;
  });
  const nextState = updatedFolders.reduce(replaceFolder, state);
  return deleteAll(nextState, toRemove);
}
function rename(state, updatedFolder) {
  const index = findIndex(state, updatedFolder.get('id'));
  const prevFolder = state.getIn(['objects', index]);
  const pathMatcher = getPathMatcher(prevFolder);
  const escapedPath = updatedFolder.get('full_path').replace(/\$/, '$$$');
  const nextFolders = state.get('objects').map(folder => {
    const path = folder.get('full_path');
    if (folder.get('id') === updatedFolder.get('id')) {
      return updatedFolder;
    } else if (pathMatcher.test(path)) {
      const nextPath = path.replace(pathMatcher, `${escapedPath}/`);
      return folder.set('full_path', nextPath);
    }
    return folder;
  });
  return state.set('objects', nextFolders);
}
function restoreOriginalFoldersOnBulkMoveFailure(state, folders) {
  if (!(folders instanceof Immutable.OrderedMap)) {
    folders = Immutable.OrderedMap(folders.map(f => [f.get('id'), f]));
  }
  return state.merge({
    objects: state.get('objects').concat(folders),
    total: state.get('total') + folders.count()
  });
}
function updateRequestInfo(state, requestType, requestLookupKey, attrs = {}) {
  attrs.timestamp = new Date().getTime();
  return state.mergeIn(['requests', requestType, requestLookupKey], attrs);
}
export default function Folders(state = defaultState, action) {
  const {
    type,
    status,
    data,
    folder,
    folders,
    updatedFolders,
    destination,
    initialFetch,
    folderIds,
    teamIds
  } = action;
  if (action.requestType && action.requestLookupKey) {
    state = updateRequestInfo(state, action.requestType, action.requestLookupKey, {
      status
    });
  }
  switch (type) {
    case SEARCH_FILES_AND_FOLDERS_ATTEMPTED:
    case FETCH_FOLDERS_ATTEMPTED:
      // after the initial fetch, its important NOT to set the top-level status to PENDING, as it will unmount the rows of the table due to getCombinedStatus selector behavior
      if (initialFetch) {
        return state.merge({
          status
        });
      }
      return state;
    case SEARCH_FILES_AND_FOLDERS_FAILED:
    case FETCH_FOLDERS_FAILED:
      // do not wipe out all the folders if a specific request fails
      if (!initialFetch) {
        return state;
      }
      return Immutable.Map({
        objects: new Immutable.OrderedMap(),
        total: 0,
        timestamp: null,
        status
      });
    case FETCH_FOLDERS_SUCCEEDED:
      // its possible to fetch hidden folders initially via the /info/multiple endpoint, which happens on the form submission redirect.
      // in this case we want to fetch and confirm the folder of that path exists, but not place in the reducer which would show it in dash table and allow it to be manipulated
      state = state.updateIn(['objects'], objects => objects.concat(data.get('objects').filter(f => !f.get('hidden'))));
      if (initialFetch || state.get('status') !== RequestStatus.SUCCEEDED) {
        state = state.merge({
          status,
          timestamp: new Date().getTime(),
          total: data.get('total') || state.get('objects').size
        });
      }
      return state;
    case SEARCH_FILES_AND_FOLDERS_SUCCESS:
      state = state.updateIn(['objects'], objects => objects.concat(data.get('folders')));
      if (initialFetch || state.get('status') !== RequestStatus.SUCCEEDED) {
        state = state.merge({
          status,
          timestamp: new Date().getTime()
        });
      }
      return state;
    case ADD_FOLDER_SUCCEEDED:
      {
        const objects = state.get('objects').set(folder.get('id'), folder);
        return state.merge({
          objects: objects.sortBy(f => f.get('name')),
          total: objects.size
        });
      }
    case DELETE_FOLDER_SUCCEEDED:
      return deleteFolder(state, folder);
    case RENAME_FOLDER_SUCCEEDED:
      return rename(state, folder);
    case MOVE_FOLDER_SUCCEEDED:
      return replaceFolder(state, folder);
    case BULK_DELETE_ATTEMPTED:
      return deleteAll(state, folders);
    case BULK_MOVE_ATTEMPTED:
      return move(state, folders, destination);
    case BULK_MOVE_SUCCEEDED:
      return handleMove(state, folders, updatedFolders);
    case BULK_MOVE_FAILED:
    case BULK_DELETE_FAILED:
      return restoreOriginalFoldersOnBulkMoveFailure(state, folders);
    case CHANGE_FOLDER_PARTITIONING_SUCCEEDED:
      return state.merge({
        objects: state.get('objects').map(folderToUpdate => {
          if (folderIds.includes(folderToUpdate.get('id'))) {
            return folderToUpdate.set('teams', Immutable.Set(teamIds));
          }
          return folderToUpdate;
        })
      });
    default:
      return state;
  }
}